import {
  getDensityUnits,
  getFixedConversionFactor,
  isVolumeUnit,
  normalizeUnit,
} from '@/lib/utils/unitConversion';
import { DISCRETE_WORKING_UNITS } from '../constants/materials.constants';

export const CONVERSION_KINDS = Object.freeze({
  fixed: 'FIXED',
  density: 'DENSITY',
  discrete: 'DISCRETE',
});

export const isDiscreteUnit = (unit) => DISCRETE_WORKING_UNITS.includes(unit);

export const conversionRule = (declarationUnit, workingUnit) => {
  if (!declarationUnit || !workingUnit) return null;

  if (isDiscreteUnit(workingUnit)) {
    return {
      kind: CONVERSION_KINDS.discrete,
      factor: null,
      locked: false,
      required: true,
      weightUnit: null,
      volumeUnit: null,
    };
  }

  const density = getDensityUnits(declarationUnit, workingUnit);
  if (density) {
    return {
      kind: CONVERSION_KINDS.density,
      factor: null,
      locked: false,
      required: false,
      weightUnit: density.weightUnit,
      volumeUnit: density.volumeUnit,
    };
  }

  const factor = getFixedConversionFactor(declarationUnit, workingUnit);
  return {
    kind: CONVERSION_KINDS.fixed,
    factor: factor === null ? '' : String(factor),
    locked: true,
    required: false,
    weightUnit: null,
    volumeUnit: null,
  };
};

export const EXAMPLE_QUANTITY = 100;

export const EXAMPLE_OPERATIONS = Object.freeze({
  multiply: 'MULTIPLY',
  divide: 'DIVIDE',
});

// Discrete factors and a weight-to-volume density are stated the other way round, so they divide.
const exampleOperation = (rule, declarationUnit) => {
  if (rule.kind === CONVERSION_KINDS.discrete) return EXAMPLE_OPERATIONS.divide;
  if (rule.kind === CONVERSION_KINDS.density && !isVolumeUnit(declarationUnit)) {
    return EXAMPLE_OPERATIONS.divide;
  }
  return EXAMPLE_OPERATIONS.multiply;
};

/** EXAMPLE_QUANTITY declaration units worked through the entered factor; null until one is usable. */
export const conversionExample = (rule, declarationUnit, workingUnit, value) => {
  const factor = Number(value);
  if (!rule || value == null || String(value).trim() === '' || !(factor > 0)) return null;

  const operation = exampleOperation(rule, declarationUnit);
  const result =
    operation === EXAMPLE_OPERATIONS.multiply
      ? EXAMPLE_QUANTITY * factor
      : EXAMPLE_QUANTITY / factor;

  return {
    quantity: EXAMPLE_QUANTITY,
    declarationUnit: normalizeUnit(declarationUnit),
    workingUnit: normalizeUnit(workingUnit),
    factor,
    operation,
    result,
  };
};
